import { Link } from 'react-router-dom';
import { FolderOpen, FileText, MoreVertical, Trash2, Edit } from 'lucide-react';
import { useState, useRef } from 'react';

export default function ProjectRow({ project, onDelete, onEdit }) {
  const [showMenu, setShowMenu] = useState(false);
  const menuButtonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${project.name}"?`)) {
      onDelete(project.id);
    }
    setShowMenu(false);
  };

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onEdit(project);
    setShowMenu(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', class: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700' },
      completed: { label: 'Completed', class: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700' },
      archived: { label: 'Archived', class: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-700' }
    };

    const config = statusConfig[status] || statusConfig.active;

    return (
      <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${config.class}`}>
        {config.label}
      </span>
    );
  };

  return (
    <Link to={`/projects/${project.id}`} className="block">
      <div className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition border-b border-gray-200 dark:border-dark-border last:border-b-0 group">
        {/* Folder Icon */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <FolderOpen className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
        </div>

        {/* Project Name & Date */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">
            {project.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Created {formatDate(project.createdAt)}
          </p>
        </div>

        {/* Files Count */}
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <FileText className="w-4 h-4" />
          <span>{project.documentCount || 0} {project.documentCount === 1 ? 'file' : 'files'}</span>
        </div>

        {/* Pages Count */}
        <div className="text-sm text-gray-600 dark:text-gray-400 min-w-[80px]">
          {project.pageCount || 0} {project.pageCount === 1 ? 'page' : 'pages'}
        </div>

        {/* Status Badge */}
        <div className="flex-shrink-0">
          {getStatusBadge(project.status)}
        </div>

        {/* Actions Menu */}
        <div className="flex-shrink-0 relative">
          <button
            ref={menuButtonRef}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!showMenu && menuButtonRef.current) {
                const rect = menuButtonRef.current.getBoundingClientRect();
                setMenuPosition({
                  top: rect.bottom + 4,
                  right: window.innerWidth - rect.right,
                });
              }
              setShowMenu(!showMenu);
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg opacity-0 group-hover:opacity-100 transition"
          >
            <MoreVertical className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </button>

          {/* Dropdown Menu */}
          {showMenu && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowMenu(false);
                }}
              />

              {/* Menu - fixed positioning to avoid parent overflow clipping */}
              <div
                className="fixed w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-20"
                style={{ top: menuPosition.top, right: menuPosition.right }}
              >
                <button
                  onClick={handleEdit}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition first:rounded-t-lg"
                >
                  <Edit className="w-4 h-4" />
                  Edit Project
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition last:rounded-b-lg"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Project
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
